import React, { useState, useEffect } from 'react'
import { CustomImageModel, PicturesApi, PredefinedFolderModel } from '../../../services/PicturesApi'
import { ImageWithAuthorization } from '../../../shared/elements/ImageWithAuthorization'
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic'
import { FileUploadModal } from '../../../shared/elements/FileUploadModal'
import { ResourceApi } from '../../../services/ResourceApi'
import { toastSuccess } from '../../../shared/toastr'
import { useOfferionModal } from '../../../shared/modal/ConfirmationModal'
import { useUserStore } from '../../../shared/store/userStore'
import { CompanyType } from '../../../shared/models'
import { useOfferionTranslation } from '../../../shared/store/hooks/useOfferionTranslation'
import { CollapsibleSection } from '../../ui/CollapsibleSection'
import { useNavigate } from 'react-router-dom'

interface PicturesSectionProps {
    onPredefinedImageAdd?: (imageId: number) => void
    onCustomImageAdd?: (imageId: number) => void
    isPictureSelection?: boolean
}

export const PicturesSection = (props: PicturesSectionProps) => {
    const isPictureSelection = props.isPictureSelection ?? false
    const { t } = useOfferionTranslation()
    const navigate = useNavigate()
    const offerionModal = useOfferionModal()

    const [predefinedFoldersPath, setPredefinedFolderPath] = useState<number[]>([])
    const [currentFolderId, setCurrentFolderId] = useState<number | null>(null)

    const [selectedPredefinedImage, setSelectedPredefinedImage] = useState<number | null>()
    const [selectedCustomImageResourceIds, setSelectedCustomImageResourceIds] = useState<number[]>([])
    const [uploadCustomImageModalVisible, setUploadCustomImageModalVisible] = useState<boolean>(false)
    const companyType = useUserStore(state => state.user.CompanyType)

    const [predefinedFolderInfo, setPredefinedFolderInfo] = useState<PredefinedFolderModel | null>(null)
    const [customImages, setCustomImages] = useState<CustomImageModel[] | null>(null)

    useEffect(() => {
        PicturesApi.getContentForPredefinedFolder(currentFolderId).then(setPredefinedFolderInfo)
    }, [currentFolderId])

    const getCustomImages = () => {
        PicturesApi.getCustomImages().then(setCustomImages)
    }

    useEffect(getCustomImages, [])

    const goToPredefinedFolder = (folderId: number) => {
        const path = [...predefinedFoldersPath, folderId]

        setSelectedPredefinedImage(null)
        setPredefinedFolderInfo(null)
        setPredefinedFolderPath(path)
        setCurrentFolderId(folderId)
    }

    const goToPreviousPredefinedFolder = () => {
        predefinedFoldersPath.pop()

        const folderId = predefinedFoldersPath.length ? predefinedFoldersPath[predefinedFoldersPath.length - 1] : null

        setSelectedPredefinedImage(null)
        setPredefinedFolderInfo(null)
        setPredefinedFolderPath([...predefinedFoldersPath])
        setCurrentFolderId(folderId)
    }

    const deleteCustomImage = (imageIds: number[]) => {
        const title = imageIds.length > 1 ? t('Settings.MyPictures.DeleteMultiple.pageTitle') : t('Settings.MyPictures.Delete.pageTitle')
        const text =
            imageIds.length > 1 ? t('Settings.MyPictures.DeleteMultiple.questionYesNo') : t('Settings.MyPictures.Delete.questionYesNo')
        const successMessage =
            imageIds.length > 1 ? t('Settings.MyPictures.Messages.successDeleteMultiple') : t('Settings.MyPictures.Messages.successDelete')

        offerionModal.open(title, text, () => {
            return PicturesApi.deleteCustomImage([...imageIds])
                .then(() => toastSuccess(successMessage))
                .then(getCustomImages)
        })
    }

    const uploadResource = async (file: File) => {
        const result = await ResourceApi.uploadResource(file)

        await PicturesApi.addCustomImage(result.resourceId)

        toastSuccess('Settings.MyPictures.Messages.successUpload')

        if (props.onCustomImageAdd) {
            props.onCustomImageAdd(result.resourceId)
        } else {
            setUploadCustomImageModalVisible(false)

            getCustomImages()
        }
    }

    const toggleImageSelection = (imageId: number) => {
        if (selectedCustomImageResourceIds.includes(imageId)) {
            const resourceIds = selectedCustomImageResourceIds.filter(id => id !== imageId)
            setSelectedCustomImageResourceIds(resourceIds)
        } else {
            setSelectedCustomImageResourceIds([...selectedCustomImageResourceIds, imageId])
        }
    }

    if (customImages == null || predefinedFolderInfo == null) return null

    const predefinedPicturesDefaultOpen = companyType === CompanyType.WindowMaker
    const customImagesDefaultOpen = !predefinedPicturesDefaultOpen && customImages.length > 0

    return (
        <main className="main import file-upload settings">
            <div className="main__header">
                <div className="main-title">{t('Settings.MyPictures.Header.pageTitle')}</div>
            </div>

            <div className="table__container curled-paper pt3">
                {/* Company predefined images */}
                {companyType === CompanyType.WindowMaker && (
                    <CollapsibleSection
                        title={<span>{t('Settings.MyPictures.Table.PredefinedPictures.title')}</span>}
                        isOpenByDefault={predefinedPicturesDefaultOpen}
                    >
                        <div className="info__container">
                            <div className="info__row-content">
                                {currentFolderId ? (
                                    <button
                                        className="button button--padded button--gray button--accept"
                                        onClick={goToPreviousPredefinedFolder}
                                    >
                                        <span className="icon icon__back"></span>
                                        <span className="button-text button-text--always-show">
                                            {t('Settings.MyPictures.Table.PredefinedPictures.buttonBack')}
                                        </span>
                                    </button>
                                ) : null}
                            </div>
                        </div>

                        <div className="image__container">
                            {predefinedFolderInfo &&
                                predefinedFolderInfo.folders.map(f => {
                                    return (
                                        <div className="image__outer image__outer--folder pointer" key={f.id}>
                                            <div className="image clickable selectable" onClick={() => goToPredefinedFolder(f.id)}>
                                                <span className="image__centered-text">{f.name}</span>
                                            </div>
                                        </div>
                                    )
                                })}

                            {predefinedFolderInfo &&
                                predefinedFolderInfo.images.map(p => {
                                    if (!isPictureSelection) {
                                        // If we're not in picture selection mode, we're probably inside of picture settings.
                                        // In that case, we can't select/deselect the image, just view it.
                                        return (
                                            <div className="image__outer" key={p.id}>
                                                <ImageWithAuthorization src={`api/predefinedImages/getPredefinedImageResource/${p.id}`} />
                                            </div>
                                        )
                                    } else {
                                        // If we're in picture selection mode, clicking the picture selects it directly.
                                        return (
                                            <div
                                                className="image__outer clickable selectable"
                                                onClick={() => props.onPredefinedImageAdd && props.onPredefinedImageAdd(p.id)}
                                                key={p.id}
                                            >
                                                <ImageWithAuthorization src={`api/predefinedImages/getPredefinedImageResource/${p.id}`} />
                                            </div>
                                        )
                                    }
                                })}
                        </div>

                        {!!selectedPredefinedImage && props.onPredefinedImageAdd && (
                            <button
                                className="button button--padded button--gray button--accept"
                                onClick={() => props.onPredefinedImageAdd && props.onPredefinedImageAdd(selectedPredefinedImage)}
                            >
                                <span className="icon icon__check-green"></span>
                                <span className="button-text button-text--always-show">
                                    {t('Settings.MyPictures.Table.UploadPicture.addPictureButton')}
                                </span>
                            </button>
                        )}
                    </CollapsibleSection>
                )}

                {/* User uploaded images */}
                {customImages.length > 0 && (
                    <CollapsibleSection
                        isOpenByDefault={customImagesDefaultOpen}
                        title={<span>{t('Settings.MyPictures.Table.MyPictures.title')}</span>}
                    >
                        <div className="image__container">
                            {customImages.map((image, index) => {
                                const id = `custom-image-${image.customImageId}`

                                // If we're not in picture selection mode, we're probably inside of picture settings.
                                //  In that case show the image with a checkbox - we can delete the images here.

                                if (!isPictureSelection) {
                                    return (
                                        <div className="image__outer clickable selectable" key={id}>
                                            <ImageWithAuthorization
                                                className="image__inner"
                                                src={`api/resources/GetResource/${image.resourceName}`}
                                                onClick={e => {
                                                    toggleImageSelection(image.resourceId)
                                                }}
                                            />

                                            <input
                                                id={id}
                                                type="checkbox"
                                                className="checkbox"
                                                checked={selectedCustomImageResourceIds.includes(image.resourceId)}
                                                onClick={e => {
                                                    toggleImageSelection(image.resourceId)
                                                }}
                                            />
                                            <label htmlFor={id} className="button button--gray checkbox-button image__check">
                                                <span className="checkbox-button__icon"></span>
                                            </label>
                                        </div>
                                    )
                                } else {
                                    // if we're in picture selection mode - clicking the picture selects it directly,
                                    // usually to add it to an offer.
                                    return (
                                        <div
                                            className="image__outer clickable selectable"
                                            onClick={() => {
                                                props.onCustomImageAdd && props.onCustomImageAdd(image.resourceId)
                                            }}
                                            key={id}
                                        >
                                            <ImageWithAuthorization
                                                className="image__inner"
                                                src={`api/resources/GetResource/${image.resourceName}`}
                                            />
                                        </div>
                                    )
                                }
                            })}
                        </div>

                        {!isPictureSelection && (
                            <div className="info__container">
                                <div className="info__row-content">
                                    {selectedCustomImageResourceIds.length > 0 && (
                                        <button
                                            className="button button--padded button--gray button--accept"
                                            onClick={() => deleteCustomImage(selectedCustomImageResourceIds)}
                                        >
                                            <span className="icon icon__delete"></span>
                                            <span className="button-text button-text--always-show">
                                                {t('Settings.MyPictures.Table.MyPictures.buttonDelete')}
                                            </span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </CollapsibleSection>
                )}

                <CollapsibleSection isOpenByDefault={true} title={<span>{t('Settings.MyPictures.Table.UploadPicture.title')}</span>}>
                    <div className="info__container file-upload__container pb2">
                        <div className="upload-form">
                            <div className="left">
                                <label
                                    htmlFor="file-upload"
                                    className="mod-margin-left-8 button button--padded button--gray file-input__button"
                                >
                                    <input
                                        id="file-upload"
                                        className="file-input left"
                                        onClick={() => setUploadCustomImageModalVisible(true)}
                                    />
                                    <span className="icon icon--small icon__upload"></span>
                                    <span className="button-text button-text--always-show">
                                        {t('Settings.MyPictures.Table.UploadPicture.addPictureButton')}
                                    </span>
                                </label>
                            </div>
                        </div>

                        {isPictureSelection && (
                            <button
                                className="button button--padded button--gray button--accept"
                                onClick={() => navigate(-1)}
                                style={{ position: 'relative', top: -2 }}
                            >
                                <span className="icon icon__delete"></span>
                                <span className="button-text button-text--always-show">{t('Allaround.Button.cancel')}</span>
                            </button>
                        )}
                    </div>
                </CollapsibleSection>

                {uploadCustomImageModalVisible && (
                    <FileUploadModal
                        titleKey={'Settings.MyPictures.Table.UploadPicture.title'}
                        footerKey={'Settings.MyPictures.Table.UploadPicture.maxPictureSize'}
                        maxSizeInKb={100}
                        onSubmit={file => uploadResource(file)}
                        close={() => setUploadCustomImageModalVisible(false)}
                    />
                )}
            </div>
        </main>
    )
}

export const PicturesContainer = () => {
    const { t } = useOfferionTranslation()

    return (
        <>
            <BreadcrumbsItem to="/settings/pictures">{t('Settings.MyPictures.Header.pageTitle')}</BreadcrumbsItem>

            <PicturesSection />
        </>
    )
}
